import { EntityId, isNewEntity, TemporalEntityId } from 'entities-next/common'
import { buildInvestment, getApiInvestmentHandlers, Investment } from 'entities-next/fundingRounds'
import { negate } from 'lodash'
import { useList, useUnmount } from 'react-use'
import { getIndexOfItem, removeItem, replaceObjectInArray } from 'utils-next/arrays'

export function useInvestmentHandlers({
  initialInvestmentList,
  handleUpdate,
  fundingRoundId,
}: {
  initialInvestmentList: Investment[]
  handleUpdate: (investments: Investment[]) => void
  fundingRoundId: EntityId
}) {
  const { apiCreateInvestment, apiDeleteInvestment, apiUpdateInvestment } = getApiInvestmentHandlers()
  const [investmentList, { push, set }] = useList(initialInvestmentList)

  const updateInvestment = async (data: Investment) => {
    const index = getIndexOfItem(investmentList, data, isSameInvestment)
    const newInvestmentData = await (data.id === TemporalEntityId.new
      ? apiCreateInvestment(data)
      : apiUpdateInvestment(data.id, data))
    const updatedList = replaceObjectInArray(investmentList, index, newInvestmentData)
    set(updatedList)
  }

  const createInvestment = () => {
    push(buildInvestment(fundingRoundId))
  }

  const deleteInvestment = async (data: Investment) => {
    const newInvestments = removeItem(investmentList, data, isSameInvestment)
    if (data.id !== TemporalEntityId.new) {
      await apiDeleteInvestment(data.id)
    }
    set(newInvestments)
  }

  useUnmount(() => handleUpdate(investmentList.filter(negate(isNewEntity))))

  return {
    investmentList,
    handlers: {
      createInvestment,
      updateInvestment,
      deleteInvestment,
    },
  }
}

function isSameInvestment(investmentA: Investment, investmentB: Investment) {
  return (
    investmentA.id === investmentB.id && investmentA.createdAt?.toISOString() === investmentB.createdAt?.toISOString()
  )
}
